import React, {useEffect, useMemo} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useTranslate from '../../../core/languages/hooks/useTranslate';
import useCurrentLanguage from '../../../core/languages/hooks/useCurrentLanguage';
import useTransLocationsGetCollectByTransportAndCityId from '../../hooks/useTransLocationsGetCollectByTransportAndCityId';
import TransportLocation from '../../types/TransportLocation';
import { getTransportLocationLabel } from '../../utils/transportLabels';
import LocationDetailsInput from './LocationDetailsInput';
import {TransportEnum} from "../../enums/TransferTransportEnum";
import HotelDetail from "../../../hotels/types/HotelDetail";

interface ArrivalLocationToProps {
  packageWizard?: boolean;
  hotelInfo?: HotelDetail;
}

const ArrivalLocationTo: React.FC<ArrivalLocationToProps> = ({packageWizard = false, hotelInfo}) => {
    const t = useTranslate();
    const languageCode = useCurrentLanguage();
    const { control, formState: { errors }, getValues, watch, setValue} = useFormContext();

    const transportRaw = TransportEnum.OTHER;
    const transport = transportRaw ? Number(transportRaw) : null;
    const city = getValues('city');
    const selectedLocationId = watch('arrivalLocationTo');
    const arrivalLocationFromId = watch('arrivalLocationFrom');
    const hasTransport = transport !== undefined && transport !== null;
    const hasFromLocation = arrivalLocationFromId !== undefined && arrivalLocationFromId !== null && arrivalLocationFromId !== '';
    const shouldExecute = hasTransport && !!city && city !== '';

    const { data: transportLocations = [], isLoading } = useTransLocationsGetCollectByTransportAndCityId(
        transport,
        city,
        { execute: shouldExecute }
    );

    // Filter locations to exclude the one selected in "From" and only show those with requiredMoreDetails true or hotel not null - only if OTHER transport
    // Order: first locations with hotel, then locations with requiredMoreDetails
    let filteredLocations = transportLocations
        .filter(
            loc => {
                // If transport is OTHER, don't filter by arrivalLocationFromId
                const excludeFromLocation = Number(transport) !== TransportEnum.OTHER && String(loc.id) === String(arrivalLocationFromId);
                if (excludeFromLocation) return false;

                // If transport is OTHER, filter by requiredMoreDetails or hotel
                if (Number(transport) === TransportEnum.OTHER) {
                    return loc.requiredMoreDetails || (loc as any).hotel;
                }
                return true;
            }
        )
        .sort((a, b) => {
            if (Number(transport) === TransportEnum.OTHER) {
                // Primero las que tienen hotel
                const aHasHotel = !!(a as any).hotel;
                const bHasHotel = !!(b as any).hotel;
                if (aHasHotel !== bHasHotel) {
                    return aHasHotel ? -1 : 1;
                }
                // Si ambas tienen o no hotel, las con requiredMoreDetails van primero
                const aRequired = a.requiredMoreDetails;
                const bRequired = b.requiredMoreDetails;
                if (aRequired !== bRequired) {
                    return aRequired ? -1 : 1;
                }
            }
            return 0;
        });

    // Find the selected location to know if it requires more details
    const selectedLocation = filteredLocations.find(loc => String(loc.id) === String(selectedLocationId));
    const requiresDetails = selectedLocation?.requiredMoreDetails || false;

    // Create a stable string key from filtered locations IDs to detect content changes
    const filteredLocationsKey = useMemo(() => filteredLocations.map(l => l.id).join(','), [filteredLocations]);

  // Auto-select the hotel location for packageWizard
  useEffect(() => {
    if (packageWizard && hotelInfo?.hotelObject.id && filteredLocations.length > 0 && !selectedLocationId) {
      const hotelId = hotelInfo.hotelObject.id;
      const hotelLocation = filteredLocations.find(loc => {
        const locHotelId = (loc as any).hotel ? parseInt((loc as any).hotel.split('/').pop()) : null;
        return locHotelId === hotelId;
      });

      if (hotelLocation) {
        setValue('arrivalLocationTo', hotelLocation.id);
      }
    }
  }, [packageWizard, hotelInfo?.hotelObject.id, filteredLocationsKey, selectedLocationId, setValue]);

    return (
        <div>
            <Controller
                name="arrivalLocationTo"
                control={control}
                rules={{ required: hasTransport && hasFromLocation ? t('common.feedback.field_required') : false }}
                render={({ field }) => (
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            {getTransportLocationLabel(t, transport, 'arrivalTo')}
                        </label>
                        <select
                            {...field}
                            disabled={!hasTransport || !hasFromLocation || isLoading || packageWizard}
                            className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg
                            bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none
                            focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">
                                {!hasTransport ? t('transfers.message.selectTransportFirst') :
                                 !hasFromLocation ? t('transfers.message.selectArrivalLocationFromFirst') :
                                 (isLoading ? t('common.actions.loading') : t('common.actions.select'))}
                            </option>
                            {filteredLocations.map((location: TransportLocation) => (
                                <option key={location.id} value={location.id}>
                                    {location.trName[languageCode] || location.trName.en}
                                </option>
                            ))}
                        </select>
                        {errors.arrivalLocationTo && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.arrivalLocationTo.message}
                            </p>
                        )}
                    </div>
                )}
            />
            {requiresDetails && (
                <LocationDetailsInput
                    locationId={selectedLocationId}
                    fieldName="arrivalLocationToDetails"
                    label={t('transfers.fields.arrivalLocationToDetails')}
                />
            )}
        </div>
    );
};

export default ArrivalLocationTo;
